import type { Audience, ScenarioSummary } from "@shared/scenario";
import { useEffect, useMemo, useState } from "preact/hooks";
import { ArtifactCard, PhoneCall, VoteBars } from "../components/game";
import { Header, Page } from "../components/Header";
import { Icon } from "../components/Icon";
import { Button } from "../components/ui";
import { useI18n } from "../i18n";
import { listScenarios } from "../lib/scenario";
import { threatClasses } from "../lib/threats";
import { TOUR_STRIP, tourFrame } from "../lib/tour";
import { Link, navigate } from "../router";
import { HERO_CALL, HERO_CALL_LINES, HERO_TEXT, HERO_VOTE } from "./heroDemo";
import { TourFigure } from "./Tour";

/** The five animated bars that stand in for "something is coming in". */
function Signal({ class: cls }: { class?: string }) {
  return (
    <span class={`sig ${cls ?? ""}`} aria-hidden="true">
      <span />
      <span />
      <span />
      <span />
      <span />
    </span>
  );
}

const STEPS = [1, 2, 3, 4] as const;

const THREATS = [
  { key: "social", icon: "phone-call" },
  { key: "deepfake", icon: "audio-lines" },
  { key: "bec", icon: "banknote" },
  { key: "ransomware", icon: "lock-keyhole" },
  { key: "thirdparty", icon: "cloud" },
  { key: "identity", icon: "key-round" },
] as const;

/** The three rooms the library is written for, in the order the catalogue lists them. */
const TRACKS: { id: Audience; icon: string }[] = [
  { id: "workplace", icon: "briefcase" },
  { id: "community", icon: "heart-handshake" },
  { id: "youth", icon: "smile" },
];

const FEATURES = [
  { key: "roles", icon: "user-check" },
  { key: "branching", icon: "flag-triangle-right" },
  { key: "injects", icon: "message-square" },
  { key: "report", icon: "file-text" },
  { key: "access", icon: "book-open-check" },
  { key: "scale", icon: "qr-code" },
] as const;

export function Landing() {
  const { t, locale } = useI18n();
  const [scenarios, setScenarios] = useState<ScenarioSummary[]>([]);
  useEffect(() => {
    // The public library is the reviewed catalogue; facilitators' shared scenarios live in the picker.
    listScenarios(locale)
      .then((r) => setScenarios(r.scenarios.filter((s) => s.origin === "catalogue")))
      .catch(() => setScenarios([]));
  }, [locale]);

  const byAudience = useMemo(() => {
    const groups = new Map<Audience, ScenarioSummary[]>();
    for (const track of TRACKS) groups.set(track.id, []);
    for (const s of scenarios) groups.get(s.audience ?? "workplace")?.push(s);
    return groups;
  }, [scenarios]);

  const figures = useMemo(() => {
    const roles = new Set<string>();
    let decisions = 0;
    for (const s of scenarios) {
      decisions += s.decisions;
      for (const r of s.roles) roles.add(`${s.id}:${r.id}`);
    }
    return [
      { value: scenarios.length, label: t("landing.stats.scenarios") },
      { value: decisions, label: t("landing.stats.decisions") },
      { value: roles.size, label: t("landing.stats.roles") },
      { value: 12, label: t("landing.stats.languages") },
    ];
  }, [scenarios, t]);

  return (
    <Page wide>
      <Header
        right={
          <Link href="/host" class="btn secondary sm">
            {t("nav.host")}
          </Link>
        }
      />
      <main id="main" class="landing">
        {/* ---------------------------------------------------------------- hero */}
        <section class="hero ink-panel">
          <div class="hero-copy">
            <p class="eyebrow hero-eyebrow">
              <Signal />
              <span>{t("landing.eyebrow")}</span>
            </p>
            <h1 class="hero-title">{t("landing.hero.title")}</h1>
            <p class="hero-lead">{t("landing.hero.body")}</p>
            <div class="row hero-actions">
              <Button size="lg" icon="phone-call" onClick={() => navigate("/host")}>
                {t("landing.cta.host")}
              </Button>
              <Button size="lg" variant="secondary" icon="qr-code" onClick={() => navigate("/join")}>
                {t("landing.cta.join")}
              </Button>
            </div>
            <p class="eyebrow hero-note">{t("landing.hero.note")}</p>
          </div>

          {/*
            Three beats of one scenario, each drawn by the component a session uses: the call comes in,
            the text with the code lands, the room votes. They are pictures, so the whole block is
            hidden from assistive technology and described once by the caption.
          */}
          <div class="hero-art">
            <p class="visually-hidden">{t("landing.hero.demoLabel")}</p>
            <div class="hero-beat hero-call" aria-hidden="true">
              <PhoneCall node={HERO_CALL} revealedLines={HERO_CALL_LINES} compact />
            </div>
            <div class="hero-beat hero-text" aria-hidden="true">
              <ArtifactCard artifact={HERO_TEXT} />
            </div>
            <div class="hero-beat hero-vote" aria-hidden="true">
              <div class="hero-vote-head">
                <span class="eyebrow">{t("screen.vote.open")}</span>
                <span class="hero-clock mono">0:24</span>
              </div>
              <VoteBars options={HERO_VOTE.options} tally={HERO_VOTE.tally} />
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------- figures */}
        {scenarios.length ? (
          <section aria-labelledby="figures-title" class="figures">
            <h2 id="figures-title" class="visually-hidden">
              {t("landing.stats.title")}
            </h2>
            <ul class="figure-row">
              {figures.map((f) => (
                <li key={f.label}>
                  <b class="figure">{f.value}</b>
                  <span class="eyebrow">{f.label}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {/* --------------------------------------------------------- how it runs */}
        <section aria-labelledby="how-title" class="lsec">
          <div class="sec-head">
            <span class="idx">01</span>
            <h2 id="how-title">{t("landing.how.title")}</h2>
            <span class="note">{t("landing.how.note")}</span>
          </div>
          <ol class="run">
            {STEPS.map((n) => (
              <li key={n}>
                <span class="run-n figure" aria-hidden="true">
                  {n}
                </span>
                <h3>{t(`landing.how.${n}.title`)}</h3>
                <p class="fg-2">{t(`landing.how.${n}.body`)}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* ------------------------------------------------------------- threats */}
        <section aria-labelledby="threats-title" class="lsec">
          <div class="sec-head">
            <span class="idx">02</span>
            <h2 id="threats-title">{t("landing.threats.title")}</h2>
            <span class="note">{t("landing.threats.note")}</span>
          </div>
          <div class="threat-grid">
            {THREATS.map((x) => (
              <article key={x.key} class="threat">
                <Icon name={x.icon} class="lg threat-icon" />
                <h3>{t(`landing.threats.${x.key}.title`)}</h3>
                <p class="fg-2 small">{t(`landing.threats.${x.key}.body`)}</p>
              </article>
            ))}
          </div>
        </section>

        {/* -------------------------------------------------------------- tracks */}
        <section aria-labelledby="tracks-title" class="lsec">
          <div class="sec-head">
            <span class="idx">03</span>
            <h2 id="tracks-title">{t("landing.tracks.title")}</h2>
            <span class="note">{t("landing.tracks.note")}</span>
          </div>
          <div class="tracks">
            {TRACKS.map((track) => {
              const n = byAudience.get(track.id)?.length ?? 0;
              return (
                <article key={track.id} class={`track ${track.id}`}>
                  <Icon name={track.icon} class="lg track-icon" />
                  <h3>{t(`audience.${track.id}`)}</h3>
                  <p class="fg-2">{t(`audience.${track.id}.body`)}</p>
                  <p class="eyebrow track-count">
                    {scenarios.length
                      ? t("landing.tracks.count", { count: n })
                      : t(`audience.${track.id}.who`)}
                  </p>
                </article>
              );
            })}
          </div>
        </section>

        {/* ------------------------------------------------------------- library */}
        {scenarios.length ? (
          <section aria-labelledby="library-title" class="lsec">
            <div class="sec-head">
              <span class="idx">04</span>
              <h2 id="library-title">{t("landing.scenarios.title")}</h2>
              <span class="note">{t("landing.scenarios.note")}</span>
            </div>
            <ol class="catalogue">
              {scenarios.map((s, i) => (
                <li key={s.id} class="cat-row">
                  <span class="cat-n mono" aria-hidden="true">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div class="cat-main">
                    <h3>{s.title}</h3>
                    <p class="fg-2">{s.tagline}</p>
                    <ul class="cat-tags">
                      <li class={`tag audience ${s.audience}`}>{t(`audience.${s.audience}`)}</li>
                      {threatClasses(s.tags, 3).map((c) => (
                        <li key={c} class="tag">
                          {t(`tag.${c}`)}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <dl class="cat-meta">
                    <div>
                      <dt class="eyebrow">{t("scenario.difficultyLabel")}</dt>
                      <dd>{t(`scenario.difficulty.${s.difficulty}`)}</dd>
                    </div>
                    <div>
                      <dt class="eyebrow">{t("scenario.lengthLabel")}</dt>
                      <dd class="num">{t("scenario.minutes", { count: s.estimatedMinutes })}</dd>
                    </div>
                    <div>
                      <dt class="eyebrow">{t("scenario.rolesLabel")}</dt>
                      <dd class="num">{s.roles.length}</dd>
                    </div>
                    <div>
                      <dt class="eyebrow">{t("scenario.decisionsLabel")}</dt>
                      <dd class="num">{s.decisions}</dd>
                    </div>
                  </dl>
                </li>
              ))}
            </ol>
          </section>
        ) : null}

        {/* ---------------------------------------------------------------- tour */}
        <section aria-labelledby="tour-title" class="lsec">
          <div class="sec-head">
            <span class="idx">05</span>
            <h2 id="tour-title">{t("landing.tour.title")}</h2>
            <span class="note">{t("landing.tour.note")}</span>
          </div>
          <div class="strip">
            {TOUR_STRIP.map((id) => (
              <TourFigure key={id} frame={tourFrame(id)} compact />
            ))}
          </div>
          <div class="row strip-actions">
            <Link href="/tour" class="btn">
              <Icon name="monitor" /> {t("landing.tour.cta")}
            </Link>
            <a href="/r/example" class="btn secondary" target="_blank" rel="noopener">
              <Icon name="file-text" /> {t("landing.tour.example")}
            </a>
          </div>
        </section>

        {/* ------------------------------------------------------------ features */}
        <section aria-labelledby="features-title" class="lsec">
          <div class="sec-head">
            <span class="idx">06</span>
            <h2 id="features-title">{t("landing.features.title")}</h2>
          </div>
          <div class="feature-grid">
            {FEATURES.map((f) => (
              <article key={f.key} class="feature">
                <Icon name={f.icon} class="feature-icon" />
                <div>
                  <h3>{t(`landing.features.${f.key}.title`)}</h3>
                  <p class="fg-2 small">{t(`landing.features.${f.key}.body`)}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* -------------------------------------------------------------- report */}
        <section aria-labelledby="report-title" class="lsec">
          <div class="sec-head">
            <span class="idx">07</span>
            <h2 id="report-title">{t("landing.report.title")}</h2>
          </div>
          <div class="report-split">
            <div class="stack">
              <p class="lead fg-2">{t("landing.report.body")}</p>
              <ul class="ticks">
                {([1, 2, 3, 4] as const).map((n) => (
                  <li key={n}>
                    <Icon name="circle-check" />
                    <span>{t(`landing.report.${n}`)}</span>
                  </li>
                ))}
              </ul>
              <div>
                <a href="/r/example" class="btn secondary" target="_blank" rel="noopener">
                  <Icon name="external-link" /> {t("landing.tour.example")}
                </a>
              </div>
            </div>
            <div class="report-art ink-panel" aria-hidden="true">
              <ReportGlyph label={t("landing.report.figureLabel")} />
            </div>
          </div>
        </section>

        {/* --------------------------------------------------------------- close */}
        <section class="close ink-panel" aria-labelledby="close-title">
          <div>
            {/*
              A static rule, not the animated signal bars: this panel's job is to be the quietest,
              most decisive block on the page, and a looping ornament next to the two buttons only
              competes with them. The rule is the same motif the section heads and the footer use,
              so the page closes on its own grammar.
            */}
            <hr class="rule close-rule" />
            <h2 id="close-title">{t("landing.close.title")}</h2>
            <p class="fg-2">{t("landing.close.body")}</p>
          </div>
          <div class="row close-actions">
            <Button size="lg" icon="phone-call" onClick={() => navigate("/host")}>
              {t("landing.cta.host")}
            </Button>
            <Button size="lg" variant="secondary" icon="qr-code" onClick={() => navigate("/join")}>
              {t("landing.cta.join")}
            </Button>
          </div>
        </section>

        <footer class="landing-footer">
          <hr class="rule" />
          <p class="muted small">{t("landing.footer")}</p>
        </footer>
      </main>
    </Page>
  );
}

/**
 * A miniature of the report's opening spread: the readiness ring and the capability bars. Drawn
 * rather than screenshotted so it restyles with the theme and stays sharp at any size.
 */
function ReportGlyph({ label }: { label: string }) {
  const bars = [100, 62, 44, 78, 28];
  const r = 52;
  const c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 320 180" role="img" aria-label={label} class="report-glyph">
      <title>{label}</title>
      <g transform="translate(70 90)">
        <circle r={r} fill="none" stroke="currentColor" stroke-opacity="0.16" stroke-width="13" />
        <circle
          r={r}
          fill="none"
          stroke="var(--signal)"
          stroke-width="13"
          stroke-linecap="round"
          stroke-dasharray={`${c * 0.64} ${c}`}
          transform="rotate(-90)"
        />
        <text
          text-anchor="middle"
          y="10"
          font-size="34"
          font-weight="800"
          fill="currentColor"
          font-family="var(--font-display)"
        >
          64
        </text>
      </g>
      <g transform="translate(160 36)">
        {bars.map((w, i) => (
          <g key={w} transform={`translate(0 ${i * 24})`}>
            <rect width="132" height="9" rx="4.5" fill="currentColor" fill-opacity="0.14" />
            <rect width={(132 * w) / 100} height="9" rx="4.5" fill="currentColor" fill-opacity="0.72" />
          </g>
        ))}
      </g>
    </svg>
  );
}
